import { useState, useEffect } from 'react';
import firebase from 'firebase/app';
import 'firebase/firestore';
import 'firebase/functions';

/** A tag's payout currency (tag.paymentDetails.currency, else a legacy top-level field). */
function tagCurrency(tagData) {
  if (!tagData) return null;
  const nested = tagData.paymentDetails && tagData.paymentDetails.currency;
  return nested || tagData.currency || null;
}

/** The tag doc's data, or null when it doesn't exist. */
async function readTag(tagId) {
  const snap = await firebase.firestore().collection('tags').doc(tagId).get();
  return snap && snap.exists ? snap.data() : null;
}

/** The template behind a tag, or null (a failed read is logged, never fatal). */
async function readTemplate(templateTagId) {
  if (!templateTagId) return null;
  try {
    const snap = await firebase.firestore().collection('issuers').doc('strike')
      .collection('template_tags').doc(templateTagId).get();
    return snap.exists ? snap.data() : null;
  } catch (err) {
    console.warn('[useLoginContext] template fetch failed:', err?.message);
    return null;
  }
}

/** Public name + logo for a team, or null when it isn't found/live. */
async function fetchPublicTeam(teamId) {
  const result = await firebase.functions()
    .httpsCallable('userService-teamPublicHandler-getPublicTeamInfo')({ teamId });
  const data = (result && result.data) || {};
  return data.found ? { teamName: data.teamName || null, topLogo: data.topLogo || null } : null;
}

/** Claim landing: the tag's (and its template's) branding and copy. */
async function loadClaimContext(tagId) {
  const tagData = await readTag(tagId);
  if (!tagData) return { type: 'claim', loading: false };
  const templateData = await readTemplate(tagData.templateTagId);
  const actionText = templateData?.actionText || tagData.actionText || {};
  return {
    type: 'claim',
    loading: false,
    // The venue's payout currency — the login screen turns it into the phone
    // picker's opening country (USD → US) for staff with no profile yet.
    currency: tagCurrency(tagData) || tagCurrency(templateData),
    actionTextHeading: actionText.heading || null,
    actionTextSubheading: actionText.subheading || null,
    product: templateData?.product || tagData.product || 'payment',
    signupImage: actionText.signupImage || null,
    topLogo: templateData?.topLogo || tagData.topLogo || null,
    avatar: templateData?.avatar || tagData.avatar || null,
    rgbColor: templateData?.rgbColor || tagData.rgbColor || null,
    fgColor: templateData?.fgColor || tagData.fgColor || null,
    templateTagId: tagData.templateTagId || null,
    tagId,
  };
}

/**
 * Join landing. A team invite names the team. A partner join link
 * (/client/join/{tagId}?xmid=…) names only the tag — and until this was handled,
 * that landing fell through to the generic "Welcome back" copy. The tag doc carries
 * teamId + teamName (readable signed-out, like a claim), so read it first and only
 * call the team function when the tag doesn't already say what the team is called.
 */
async function loadJoinContext({ tagId, teamId }) {
  let resolvedTeamId = teamId || null;
  let info = null;
  let currency = null;
  if (!resolvedTeamId && tagId) {
    const tagData = await readTag(tagId);
    resolvedTeamId = (tagData && tagData.teamId) || null;
    currency = tagCurrency(tagData);
    if (tagData && tagData.teamName) info = { teamName: tagData.teamName, topLogo: tagData.topLogo || null };
  }
  if (!info && resolvedTeamId) info = await fetchPublicTeam(resolvedTeamId);
  return {
    type: 'join',
    loading: false,
    teamName: (info && info.teamName) || null,
    topLogo: (info && info.topLogo) || null,
    teamId: resolvedTeamId,
    tagId: tagId || null,
    currency,
  };
}

/** Which loader a landing needs, with its loading placeholder and failure fallback. */
function loaderFor(reason, tagId, teamId) {
  if (reason === 'claim_tag' && tagId) {
    return {
      type: 'claim',
      load: () => loadClaimContext(tagId),
      fallback: { type: 'claim', loading: false },
      failure: 'tag fetch failed',
    };
  }
  if (reason === 'join_team' && (teamId || tagId)) {
    return {
      type: 'join',
      load: () => loadJoinContext({ tagId, teamId }),
      fallback: {
        type: 'join', loading: false, teamId: teamId || null, tagId: tagId || null,
      },
      failure: 'team fetch failed',
    };
  }
  return null;
}

/**
 * Fetches display context for the login page based on the current URL intent.
 * Returns tag/team branding (name, logo, colors) so the login page can show
 * contextual messaging like "Claim X" or "Join Y".
 *
 * Display is always URL-driven — never reads stale data from the user doc.
 */
export function useLoginContext(originContext) {
  const [context, setContext] = useState({ type: 'default', loading: false });

  const reason = originContext?.reason;
  const tagId = originContext?.params?.tagId;
  const teamId = originContext?.params?.teamId;

  useEffect(() => {
    let cancelled = false;
    const loader = loaderFor(reason, tagId, teamId);
    if (!loader) {
      setContext({ type: 'default', loading: false });
      return undefined;
    }
    setContext((prev) => ({ ...prev, type: loader.type, loading: true }));
    loader.load()
      .then((next) => { if (!cancelled) setContext(next); })
      .catch((err) => {
        console.warn(`[useLoginContext] ${loader.failure}:`, err?.message);
        if (!cancelled) setContext(loader.fallback);
      });
    return () => { cancelled = true; };
  }, [reason, tagId, teamId]);

  return context;
}
